import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  IconButton
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ForcePasswordResetPage = () => {
  const { user, tokens, authorizedFetch, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!user) {
      setError("You must be signed in to change your password.");
      return;
    }
    if (password.trim().length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authorizedFetch("/api/auth/password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password })
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to update password.";
        throw new Error(message);
      }

      setSession(
        {
          user: { ...user, forcePasswordReset: false },
          tokens: tokens
        },
        undefined
      );

      setSuccess("Password updated. You will be redirected.");
      const destination = (location.state as { from?: string } | null)?.from || "/player";
      setTimeout(() => navigate(destination, { replace: true }), 500);
    } catch (err) {
      console.error("Failed to update password", err);
      setError(err instanceof Error ? err.message : "Unable to update password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="section" maxWidth={480} mx="auto">
      <Typography variant="h4" gutterBottom>
        Update your password
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Your account requires a password reset before proceeding.
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "grid", gap: 2, mt: 2 }}>
        <TextField
          label="New password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          label="Confirm new password"
          type={showConfirm ? "text" : "password"}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm((prev) => !prev)} edge="end">
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Button type="submit" variant="contained" disabled={submitting}>
          Save password
        </Button>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}
      </Box>
    </Box>
  );
};

export default ForcePasswordResetPage;
